import type { FieldInputProps } from 'formik';
import { useCallback } from 'react';

import { PreferredUnitSystem, PREFERRED_UNIT_SYSTEMS } from '@frontend/api';
import {
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  NumberInput,
  NumberInputField,
} from '@frontend/legacy-components';

import { mmToInches, inchesToMm } from 'utils/helpers';

import type { FormInputProps, NestingDefaultsData } from './types';

type NumberedFormControlProps = {
  icon1?: React.ReactNode;
  icon2?: React.ReactNode;
  field: FieldInputProps<string>;
  form: FormInputProps<NestingDefaultsData>;
  step?: number;
  min?: number;
  ariaLabel?: string;
  format?: (value: string) => string;
  parse?: (value: string) => string | null;
  [x: string]: unknown;
};

type MeasuredUnitFormControlProps = {
  icon1?: React.ReactNode;
  icon2?: React.ReactNode;
  field: FieldInputProps<string>;
  form: FormInputProps<NestingDefaultsData>;
  ariaLabel?: string;
  [x: string]: unknown;
  unitSystem: PreferredUnitSystem;
};

export const NumberedFormControl = ({
  icon1,
  icon2,
  field,
  form,
  step,
  min,
  ariaLabel,
  format = (value) => value,
  parse = (value) => value,
  ...rest
}: NumberedFormControlProps) => {
  return (
    <InputGroup>
      {icon1 && <InputLeftAddon>{icon1}</InputLeftAddon>}
      <NumberInput
        step={step || 1}
        min={min || 0}
        defaultValue={format(field.value)}
        onBlur={(event) =>
          form.setFieldValue(field.name, parse(event?.target.value), true)
        }
        aria-label={ariaLabel}
        {...rest}
      >
        <NumberInputField />
      </NumberInput>
      {icon2 && <InputRightAddon gap={0.25}>{icon2}</InputRightAddon>}
    </InputGroup>
  );
};

export const MeasuredUnitFormControl = ({
  unitSystem,
  ...props
}: MeasuredUnitFormControlProps) => {
  const format = useCallback(
    (value: string) => {
      if (isNaN(Number(value)) || !value) {
        return '';
      }
      if (unitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL) {
        return mmToInches(parseFloat(value)).toString();
      }
      return value;
    },
    [unitSystem],
  );

  const parse = useCallback(
    (value: string) => {
      if (isNaN(Number(value)) || !value) {
        return null;
      }
      if (unitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL) {
        return inchesToMm(parseFloat(value)).toString();
      }
      return value;
    },
    [unitSystem],
  );

  return (
    <NumberedFormControl
      format={format}
      parse={parse}
      step={unitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL ? 0.125 : 0.1}
      min={0}
      {...props}
    />
  );
};
